import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { CheckCircle, BadgeCheck, ArrowRight, Shield, Globe, Mail, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";

export default function Pricing() {
  return (
    <Layout>
      <Helmet>
        <title>Pricing — Transparency Badge</title>
        <meta name="description" content="Check any website for free — forever. Website owners can get a Verified Badge for $10/month (first month free). No hidden fees, cancel anytime." />
        <meta property="og:title" content="Pricing — Transparency Badge" />
        <meta property="og:description" content="Free website safety checker. Verified Badge for owners at $10/month with the first month free. No hidden fees, cancel anytime." />
        <meta property="og:url" content="https://transparencybadge.com/pricing" />
        <link rel="canonical" href="https://transparencybadge.com/pricing" />
      </Helmet>

      {/* Hero */}
      <section className="border-b bg-gradient-to-b from-accent/50 to-background py-16 md:py-20">
        <div className="container">
          <SectionHeader
            as="h1"
            badge="Pricing"
            title="Simple, Transparent Pricing"
            description="The website safety checker is free for everyone. The Verified Badge is for website owners who want to prove their site is legitimate."
          />
        </div>
      </section>

      {/* Two product section */}
      <section className="py-16 md:py-20">
        <div className="container max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">

            {/* Free checker */}
            <div className="bg-card border rounded-2xl overflow-hidden">
              <div className="bg-muted px-6 py-5">
                <div className="flex items-center gap-2 mb-1">
                  <Globe className="h-5 w-5 text-foreground" />
                  <span className="font-semibold text-foreground text-lg">Website Safety Checker</span>
                </div>
                <span className="text-xs bg-safe/20 text-safe px-2 py-0.5 rounded-full font-medium">For Everyone</span>
              </div>
              <div className="p-6 space-y-5">
                <div>
                  <p className="text-4xl font-bold text-foreground">Free</p>
                  <p className="text-sm text-muted-foreground mt-1">Forever · No registration · No limits</p>
                </div>
                <div className="space-y-2.5 pt-2 border-t">
                  {[
                    "Check any website instantly",
                    "Threat intelligence database scan",
                    "Domain age verification",
                    "SSL certificate validation",
                    "Server location lookup",
                    "No account required",
                    "Unlimited checks — check as many as you want",
                  ].map((f) => (
                    <div key={f} className="flex items-center gap-2.5">
                      <CheckCircle className="h-4 w-4 text-safe shrink-0" />
                      <span className="text-sm text-foreground">{f}</span>
                    </div>
                  ))}
                </div>
                <Button asChild className="w-full btn-orange">
                  <a href="/#check-url"><Search className="mr-2 h-4 w-4" />Check a Website Now — Free</a>
                </Button>
              </div>
            </div>

            {/* Verified Badge */}
            <div className="bg-card border-2 border-primary rounded-2xl overflow-hidden shadow-elevated">
              <div className="bg-primary px-6 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <BadgeCheck className="h-5 w-5 text-white" />
                    <span className="font-semibold text-white text-lg">Verified Badge</span>
                  </div>
                  <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded-full font-medium">For Website Owners</span>
                </div>
              </div>
              <div className="p-6 space-y-5">
                <div>
                  <div className="flex items-end gap-2">
                    <p className="text-4xl font-bold text-foreground">$10</p>
                    <p className="text-muted-foreground mb-1">/month per domain</p>
                  </div>
                  <p className="text-sm text-safe font-semibold mt-1">✓ First month completely free</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Cancel anytime · No contracts</p>
                </div>
                <div className="space-y-2.5 pt-2 border-t">
                  {[
                    "One-time eligibility check against published criteria",
                    "Automatic threat intelligence scan",
                    "Your checker result changes to 'Verified'",
                    "Embeddable trust badge for your website",
                    "Public verification page visitors can click",
                    "Continuous monthly monitoring",
                    "Badge revoked if safety standards violated",
                    "Email notification on approval and renewal",
                    "No setup fees — no hidden charges",
                    "Multiple domains supported",
                  ].map((f) => (
                    <div key={f} className="flex items-center gap-2.5">
                      <CheckCircle className="h-4 w-4 text-safe shrink-0" />
                      <span className="text-sm text-foreground">{f}</span>
                    </div>
                  ))}
                </div>
                <Button asChild className="btn-verified w-full" size="lg">
                  <Link to="/get-verified">
                    Apply Now — First Month Free
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  No payment until your site passes review. Rejected applications are never charged.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* How billing works */}
      <section className="py-16 md:py-20 bg-muted/30 border-y">
        <div className="container max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-foreground text-center mb-10">How Billing Works — Step by Step</h2>
          <div className="space-y-4">
            {[
              {
                step: 1,
                title: "Submit your application — free",
                desc: "Fill out the form with your website details. No payment info needed at this stage.",
              },
              {
                step: 2,
                title: "We check your site — 1–2 business days",
                desc: "A one-time eligibility check against our published criteria: legitimacy, safety, and compliance.",
              },
              {
                step: 3,
                title: "If approved — your badge activates, free month starts",
                desc: "You get your badge embed code by email. Month 1 is completely free.",
              },
              {
                step: 4,
                title: "Month 2 onward — $10/month",
                desc: "Billing starts from the second month. Cancel before then and you pay nothing.",
              },
              {
                step: 5,
                title: "If rejected — you're never charged",
                desc: "If your site doesn't pass review, we tell you why and you owe nothing.",
              },
            ].map((item) => (
              <div key={item.step} className="flex items-start gap-4 bg-card border rounded-xl p-5">
                <span className="w-8 h-8 rounded-full bg-primary text-white text-sm font-bold flex items-center justify-center shrink-0">
                  {item.step}
                </span>
                <div>
                  <p className="font-medium text-foreground">{item.title}</p>
                  <p className="text-sm text-muted-foreground mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20">
        <div className="container max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-foreground text-center mb-10">Pricing Questions</h2>
          <div className="space-y-4">
            {[
              {
                q: "Do I need a credit card to apply?",
                a: "No. You apply, we review your site, and only if approved do we set up billing. You pay nothing upfront.",
              },
              {
                q: "Can I cancel before I'm charged?",
                a: "Yes. Cancel during the free first month and you pay nothing. Cancel during a paid month and your badge stays active until the end of that billing period.",
              },
              {
                q: "What if my site gets rejected?",
                a: "You're never charged. We email you with the reason and what you can improve before reapplying.",
              },
              {
                q: "Can I verify multiple domains?",
                a: "Yes. Each domain is a separate $10/month subscription. Each one gets its own review and badge.",
              },
              {
                q: "Do you offer annual billing?",
                a: "Not yet — monthly only for now. Annual plans may come later.",
              },
              {
                q: "What payment methods do you accept?",
                a: "All major credit/debit cards and PayPal through our payment processor.",
              },
            ].map((item) => (
              <div key={item.q} className="border rounded-xl p-5 space-y-2 bg-card">
                <p className="font-medium text-foreground">{item.q}</p>
                <p className="text-sm text-muted-foreground">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-12 border-t bg-muted/30">
        <div className="container max-w-2xl mx-auto text-center space-y-4">
          <Mail className="h-8 w-8 text-primary mx-auto" />
          <h3 className="text-lg font-bold text-foreground">Billing Questions?</h3>
          <p className="text-muted-foreground text-sm">
            Email us at{" "}
            <a href="mailto:hello@transparencybadge.com" className="text-primary hover:underline font-medium">
              hello@transparencybadge.com
            </a>{" "}
            — we respond within 3–5 business days.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-2">
            <Link to="/refund-policy" className="text-sm text-primary hover:underline">Refund Policy</Link>
            <Link to="/terms" className="text-sm text-primary hover:underline">Terms of Service</Link>
            <Link to="/get-verified" className="text-sm text-primary hover:underline">Apply for Badge</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
